import { OptionItem } from 'components/elements/common'
import SelectDamagedPartGroup from 'components/elements/form/DamagedPartGroup/SelectDamagedPartGroup'
import RadioButtonGroup from 'components/elements/form/RadioButtonGroup'
import SelectDamagedImageGroup from 'components/elements/form/SelectDamagedImageGroup'
import SwitchButtonGroup from 'components/elements/form/SwitchButtonGroup'
import PhotoPointNote from 'components/elements/items/PhotoPointNote'
import SelectDestinationContent from 'components/elements/items/SelectDestinationContent'
import SelectVehicleModificationContent from 'components/elements/items/SelectVehicleModificationContent'
import RequestTemplate from 'components/templates/RequestTemplate'
import { WARNING } from 'constants/messages'
import { PAGE_PATH } from 'constants/pagePath'
import { VEHICLE_STATE_FORM } from 'constants/qnState'
import React from 'react'
import {
  AccidentImage,
  AccidentImageKeys,
  AccidentPartInfo,
  AccidentStatusTypes,
  DestinationTypes,
  ImageInfo,
  initialQnEditState,
  QnStatesProps,
} from 'states/QnState'
import {
  StatesProps,
  useEditQnState,
  useProgressPoint,
} from 'utils/commonHooks'
import { isSjDomain } from 'utils/domain'
import { success } from 'utils/status'

const accidentStatusOptions: Array<OptionItem<
  AccidentStatusTypes
>> = Object.entries(VEHICLE_STATE_FORM.accidentStatus.options).map(
  ([k, v]) =>
    ({ label: v as AccidentStatusTypes, value: k } as OptionItem<
      AccidentStatusTypes
    >)
)

/**
 * QN-03-d: 事故
 */
const Accident: React.FC<StatesProps> = (props: QnStatesProps) => {
  const { state, setState } = props
  const [editState, setEditingItem, setVehicleStateInfo] = useEditQnState()
  const {
    accidentImage,
    accidentPart,
    noPhoto,
    destinationType,
    isNotApplicableParts,
  } = editState.vehicleStateInfo

  // バリデーション
  const isDisabled = React.useMemo(() => {
    const hasAccidentEvidence = noPhoto
      ? // 組み合わせの損傷箇所が一つでも選択されていればtrue
        accidentPart.parts.length > 0
      : // 必須の画像選択なしでfalse
        !!accidentImage.damagedPart.image && !!accidentImage.peripheral.image

    if (destinationType === DestinationTypes.specify) {
      // 写真以外で状況説明 & ご存知のディーラー・修理工場など予定
      return (
        // タイヤ軸が複数ある車両でないかつ、事故証拠がない もしくは搬送先指定なしの場合はtrue
        (!isNotApplicableParts && !hasAccidentEvidence) ||
        !editState.destinationLocation.isSelected
      )
    } else {
      // タイヤ軸が複数ない & 事故画像なし or 損傷個所なしはtrue
      return !isNotApplicableParts && !hasAccidentEvidence
    }
  }, [editState])

  // 警告メッセージ
  const warning = React.useMemo(() => {
    switch (editState.vehicleStateInfo.destinationType) {
      case DestinationTypes.specify:
        return WARNING.WRECKER
      case DestinationTypes.introduction:
        return WARNING.INTRODUCTION
    }
    return ''
  }, [editState.vehicleStateInfo.destinationType])

  // 決定押下時の遷移先
  const forwardTo = React.useMemo((): string => {
    return state.confirmationChangeType
      ? PAGE_PATH.CONFIRMATION
      : PAGE_PATH.TROUBLE_PLACE
  }, [])

  const handleImageChanged = React.useCallback(
    (name: AccidentImageKeys, imageInfo: ImageInfo) => {
      const accidentImage: AccidentImage = {
        ...editState.vehicleStateInfo.accidentImage,
      }
      accidentImage[name] = imageInfo
      setVehicleStateInfo({
        accidentImage,
      })
    },
    [editState, setVehicleStateInfo]
  )

  const handleNoPhotoChanged = React.useCallback(
    (value: boolean) => {
      setVehicleStateInfo({
        noPhoto: value,
      })
    },
    [setVehicleStateInfo]
  )

  const handleAccidentStatusChanged = React.useCallback(
    (value: AccidentStatusTypes) => {
      setVehicleStateInfo({
        accidentStatus: value,
      })
    },
    [setVehicleStateInfo]
  )

  const handleDamagedPartChanged = React.useCallback(
    (value: AccidentPartInfo): void => {
      setVehicleStateInfo({
        accidentPart: value,
      })
    },
    [setVehicleStateInfo]
  )

  const handleIsMotorbikeChanged = React.useCallback(
    (value: boolean) => {
      setVehicleStateInfo({
        isMotorbike: value,
        accidentPart: {
          ...initialQnEditState.vehicleStateInfo.accidentPart,
        },
      })
    },
    [setVehicleStateInfo]
  )

  const handleIsNotApplicablePartsChanged = React.useCallback(
    (value: boolean) => {
      setVehicleStateInfo({
        isNotApplicableParts: value,
      })
    },
    [setVehicleStateInfo]
  )

  const forwardAction = React.useCallback(() => {
    setState({
      ...state,
      troubleDatetime: editState.troubleDatetime,
      vehicleState: editState.vehicleState ?? '',
      vehicleStateInfo: editState.vehicleStateInfo,
      destinationLocation: editState.destinationLocation,
    })
    return success()
  }, [
    editState.vehicleState,
    editState.vehicleStateInfo,
    editState.troubleDatetime,
    editState.destinationLocation,
  ])

  /** 戻るボタン押下時にeditingItemの中身をqnStateの内容と同期させる */
  const backAction = React.useCallback(() => {
    if (state.confirmationChangeType) {
      setEditingItem({
        ...editState,
        vehicleStateInfo: state.vehicleStateInfo,
        troubleDatetime: state.troubleDatetime,
        vehicleState: state.vehicleState,
        destinationLocation: state.destinationLocation,
      })
    } else {
      setState({
        ...state,
        vehicleStateInfo: initialQnEditState.vehicleStateInfo,
        destinationLocation: initialQnEditState.destinationLocation,
      })
      setEditingItem({
        ...editState,
        vehicleStateInfo: initialQnEditState.vehicleStateInfo,
        destinationLocation: initialQnEditState.destinationLocation,
      })
    }
    return success()
  }, [
    state.confirmationChangeType,
    state.vehicleStateInfo,
    state.destinationLocation,
  ])

  return (
    <RequestTemplate
      title={'事故'}
      showContact
      progress={useProgressPoint()}
      description={
        '作業のための装備、レッカーの必要性などを判断するため、以下の情報を教えてください。'
      }
      content={
        <>
          <SelectDamagedImageGroup
            accidentImage={editState.vehicleStateInfo.accidentImage}
            title={`${VEHICLE_STATE_FORM.accidentImage.formName}を2枚撮影`}
            isRequired={!editState.vehicleStateInfo.noPhoto}
            onChanged={handleImageChanged}
          />
          <SwitchButtonGroup
            title={`${VEHICLE_STATE_FORM.noPhoto.formName}は選択`}
            isSwitched={editState.vehicleStateInfo.noPhoto}
            label={`${VEHICLE_STATE_FORM.noPhoto.label}`}
            onChanged={handleNoPhotoChanged}
          />
          <PhotoPointNote />
          <RadioButtonGroup
            title={`${VEHICLE_STATE_FORM.accidentStatus.formName}を選択`}
            isRequired={true}
            options={accidentStatusOptions}
            selectedValue={editState.vehicleStateInfo.accidentStatus}
            onValueChanged={handleAccidentStatusChanged}
          />
          <SelectVehicleModificationContent />
          <SelectDestinationContent />
          {editState.vehicleStateInfo.noPhoto && (
            <>
              <SelectDamagedPartGroup
                title={`${VEHICLE_STATE_FORM.accidentPart.formName}を選択（複数選択可）`}
                isRequired
                damagedStatus={{
                  parts: editState.vehicleStateInfo.accidentPart.parts,
                  existsDamagedTire:
                    editState.vehicleStateInfo.accidentPart.existsDamagedTire,
                }}
                existsDamagedTireLabel={`${VEHICLE_STATE_FORM.accidentPart.label}`}
                isMotorbike={editState.vehicleStateInfo.isMotorbike}
                onChanged={(value: AccidentPartInfo): void => {
                  handleDamagedPartChanged(value)
                }}
              />
              {isSjDomain() && (
                <>
                  <SwitchButtonGroup
                    title={`${VEHICLE_STATE_FORM.motorbikeAccident.formName}`}
                    isSwitched={editState.vehicleStateInfo.isMotorbike}
                    label={`${VEHICLE_STATE_FORM.motorbikeAccident.label}`}
                    onChanged={handleIsMotorbikeChanged}
                  />
                  <SwitchButtonGroup
                    title={`${VEHICLE_STATE_FORM.notApplicableParts.formName}`}
                    isSwitched={editState.vehicleStateInfo.isNotApplicableParts}
                    label={`${VEHICLE_STATE_FORM.notApplicableParts.label}`}
                    onChanged={handleIsNotApplicablePartsChanged}
                  />
                </>
              )}
            </>
          )}
        </>
      }
      warning={warning}
      forwardTo={forwardTo}
      showBack
      forwardAction={forwardAction}
      backAction={backAction}
      backTo={PAGE_PATH.VEHICLE_STATE}
      notClearEditingItem
      isDisabled={isDisabled}
    />
  )
}

export default Accident
